import { Stack } from "@mui/material";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DropdownSingleSelect } from "../../../../../components";
import { Button } from "../../../../../components/ButtonNew";
import { TextFieldInput } from "../../../../../components/Input";
import { LDSToast } from "../../../../../components/LDSToast";
import { hint } from "../../../../../utils/textSymbols";
import { SingleBuildingContext } from "../../context/AddSingleBuildingContext";
import { isMixedBuilding, MIX_USE_KEYS, SUMMARY_BUILDING_TYPE_OPTIONS } from "../../model/mix";
import { SummaryStepDetailsContentProps } from "../../model/types";

export const SummaryStepDetailsForm = ({
  detailsBuildingType,
  mixConfiguration,
  onOpenMixConfiguration,
  updateDetailsField,
}: SummaryStepDetailsContentProps) => {
  const { t } = useTranslation();

  const buildingTypeDisplayOptions = useMemo(
    () => SUMMARY_BUILDING_TYPE_OPTIONS.map((value) => t(`AddBuilding.summaryStep.buildingTypeLabels.${value}`)),
    [t]
  );

  const mixConfigurationSummary = useMemo(() => {
    return MIX_USE_KEYS.filter((key) => mixConfiguration[key] > 0)
      .map((key) => `${t(`AddBuilding.summaryStep.buildingTypeLabels.${key}`)} ${mixConfiguration[key]}%`)
      .join(", ");
  }, [mixConfiguration, t]);

  const { formData } = useContext(SingleBuildingContext);
  const details = formData.details;

  const formItems = [
    {
      id: "construction_year",
      label: t("AddBuilding.summaryStep.fields.constructionYear.label"),
      placeholder: t("AddBuilding.summaryStep.fields.constructionYear.placeholder"),
      value: details?.construction_year ?? "",
    },
    {
      id: "rental_units",
      label: t("AddBuilding.summaryStep.fields.rentalUnits.label"),
      placeholder: t("AddBuilding.summaryStep.fields.rentalUnits.placeholder"),
      value: details?.rental_units ?? "",
    },
    {
      id: "no_of_floors",
      label: t("AddBuilding.summaryStep.fields.floors.label"),
      placeholder: t("AddBuilding.summaryStep.fields.floors.placeholder"),
      value: details?.no_of_floors ?? "",
    },
  ] as const;

  return (
    <Stack className="w-full flex-col items-start justify-between">
      <Stack className="mt-7 w-full flex-col gap-6">
        <Stack className="w-full flex-col items-start gap-2">
          <span className="text-lds-foreground-primary font-ds-body-bold-md">
            {t("AddBuilding.summaryStep.buildingTypeLabel")}
          </span>
          <DropdownSingleSelect
            className="w-full"
            clearable={false}
            disabled={false}
            displayOptions={buildingTypeDisplayOptions}
            hideLabel
            label=""
            onChange={() => {}}
            options={[...SUMMARY_BUILDING_TYPE_OPTIONS]}
            optionsGroups={[]}
            selectedValue={detailsBuildingType ?? ""}
            setSelectedValue={(value: string | undefined) => updateDetailsField("building_type", value ?? "")}
            width="100%"
          />
        </Stack>
        {isMixedBuilding(detailsBuildingType) && (
          <div className="w-full rounded-xl px-4 py-3 border-ds-subtle">
            <Stack className="w-full justify-between gap-2">
              <p className="font-ds-headline-md">{t("AddBuilding.summaryStep.mixConfigurationTitle")}</p>
              <Button className="font-ds-headline-md" onClick={onOpenMixConfiguration} variant="tertiary">
                {t("AddBuilding.summaryStep.mixEdit")}
              </Button>
            </Stack>
            <p className="mt-1 text-lds-foreground-muted font-ds-body-regular-md">
              {mixConfigurationSummary || t("AddBuilding.summaryStep.mixEmptyHint")}
            </p>
          </div>
        )}
        {formItems.map((item) => (
          <TextFieldInput
            className="w-full"
            height="4rem"
            id={item.id}
            key={item.id}
            label={item.label}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateDetailsField(item.id, e.target.value)}
            placeholder={item.placeholder}
            rootClassName="w-full"
            type="number"
            value={item.value}
          />
        ))}
      </Stack>
      <LDSToast
        className="mt-7 w-full"
        invisibleOnDismiss
        message={`${hint} ${t("AddBuilding.summaryStep.detailsInfoToast")}`}
      />
    </Stack>
  );
};
